/**
 * Parse, serialize, and format helpers for structured notes.
 *
 * Persistence rules:
 * - Plain notes stay as the raw string (backward compatible).
 * - Structured notes are stored as JSON of `{ originalNote, structuredNote }`.
 * - On load, invalid JSON or wrong shape falls back to plain text.
 */

import type {
  ParsedNoteValue,
  StructuredNoteFieldState,
  StructuredNotePayload,
  StructuredNoteSections,
} from './types';
import { EMPTY_STRUCTURED_NOTE } from './types';
import { parseStructuredNotePayload } from '@/utils/structuredNoteSchema';

/** Ensures every section item has defined string / null fields. */
function normalizeSections(sections: StructuredNoteSections): StructuredNoteSections {
  return {
    keyTakeaways: sections.keyTakeaways.map((item) => ({ text: item.text ?? '' })),
    agreementsDecisions: sections.agreementsDecisions.map((item) => ({ text: item.text ?? '' })),
    nextSteps: sections.nextSteps.map((item) => ({
      task: item.task ?? '',
      responsible: item.responsible ?? null,
      deadline: item.deadline ?? null,
    })),
  };
}

/**
 * Interprets a stored note string as either plain text or a structured payload.
 *
 * @param raw - Value from the form / API note field
 * @returns Discriminated parse result (`mode: 'plain' | 'structured'`)
 */
export function parseNoteValue(raw: string | null | undefined): ParsedNoteValue {
  const trimmed = raw?.trim() ?? '';
  if (!trimmed) {
    return { mode: 'plain', content: '' };
  }

  if (!trimmed.startsWith('{')) {
    return { mode: 'plain', content: raw ?? '' };
  }

  try {
    const parsed = JSON.parse(trimmed);
    const validated = parseStructuredNotePayload(parsed);
    if (validated.success) {
      return {
        mode: 'structured',
        payload: {
          originalNote: { content: validated.data.originalNote.content },
          structuredNote: normalizeSections(validated.data.structuredNote),
        },
      };
    }
  } catch {
    // fall through to plain text
  }

  return { mode: 'plain', content: raw ?? '' };
}

/**
 * Builds the JSON string persisted when AI structuring has been used.
 *
 * @param originalContent - Free-text original note
 * @param structuredNote - Current structured sections
 */
export function serializeStructuredNote(
  originalContent: string,
  structuredNote: StructuredNoteSections,
): string {
  const payload: StructuredNotePayload = {
    originalNote: { content: originalContent },
    structuredNote: normalizeSections(structuredNote),
  };
  return JSON.stringify(payload);
}

/**
 * Converts field UI state into the string written to the parent form.
 * Plain compose mode keeps a plain string; otherwise serializes the full payload.
 */
export function serializeNoteState(state: Pick<StructuredNoteFieldState, 'originalContent' | 'structuredNote' | 'viewMode'>): string {
  if (state.viewMode === 'compose-plain') {
    return state.originalContent;
  }
  return serializeStructuredNote(state.originalContent, state.structuredNote);
}

/**
 * Returns true when the user changed the structured note after the last AI generation.
 * Used to warn before regenerating / overwriting edits.
 */
export function isStructuredNoteDirty(
  structuredNote: StructuredNoteSections,
  lastAiSnapshot: string | null,
): boolean {
  if (!lastAiSnapshot) return false;
  return JSON.stringify(normalizeSections(structuredNote)) !== lastAiSnapshot;
}

/**
 * Creates a comparable snapshot of structured sections after a successful AI call.
 */
export function createAiSnapshot(structuredNote: StructuredNoteSections): string {
  return JSON.stringify(normalizeSections(structuredNote));
}

/** Trims and drops empty text items. */
function formatTextItems(items: Array<{ text: string }>): string[] {
  return items.map((item) => item.text.trim()).filter(Boolean);
}

/** Formats next steps as `task (responsible) — deadline` lines. */
function formatNextSteps(items: StructuredNoteSections['nextSteps']): string[] {
  return items
    .map((item) => {
      const task = item.task.trim();
      if (!task) return '';
      const parts = [task];
      if (item.responsible?.trim()) {
        parts.push(`(${item.responsible.trim()})`);
      }
      if (item.deadline?.trim()) {
        parts.push(`— ${item.deadline.trim()}`);
      }
      return parts.join(' ');
    })
    .filter(Boolean);
}

/**
 * Turns structured sections into a multi-line, human-readable summary.
 *
 * @param structuredNote - Sections to format
 * @param labels - Localized section headings
 */
export function formatStructuredNoteSections(
  structuredNote: StructuredNoteSections,
  labels: {
    keyTakeaways: string;
    agreementsDecisions: string;
    nextSteps: string;
  },
): string {
  const sections: string[] = [];

  const takeaways = formatTextItems(structuredNote.keyTakeaways);
  if (takeaways.length > 0) {
    sections.push(`${labels.keyTakeaways}:\n${takeaways.map((line) => `• ${line}`).join('\n')}`);
  }

  const agreements = formatTextItems(structuredNote.agreementsDecisions);
  if (agreements.length > 0) {
    sections.push(`${labels.agreementsDecisions}:\n${agreements.map((line) => `• ${line}`).join('\n')}`);
  }

  const steps = formatNextSteps(structuredNote.nextSteps);
  if (steps.length > 0) {
    sections.push(`${labels.nextSteps}:\n${steps.map((line) => `• ${line}`).join('\n')}`);
  }

  return sections.join('\n\n');
}

/**
 * Returns a human-readable string for tooltips, action logs, and compact previews.
 * Plain notes are returned as-is; structured notes are formatted into sections.
 *
 * @param raw - Stored note field value
 * @param labels - Optional localized section headings
 */
export function getNoteDisplayText(
  raw: string | null | undefined,
  labels?: {
    keyTakeaways: string;
    agreementsDecisions: string;
    nextSteps: string;
  },
): string {
  const parsed = parseNoteValue(raw);
  if (parsed.mode === 'plain') {
    return parsed.content;
  }

  const defaultLabels = labels ?? {
    keyTakeaways: 'Key Takeaways',
    agreementsDecisions: 'Agreements / Decisions',
    nextSteps: 'Next Steps',
  };

  const formatted = formatStructuredNoteSections(parsed.payload.structuredNote, defaultLabels);
  return formatted || parsed.payload.originalNote.content;
}

/**
 * Builds localized section headings from the `common` i18n namespace.
 *
 * @param t - Translator for keys under `structuredNote.sections.*`
 */
export function createStructuredNoteDisplayLabels(
  t: (key: string) => string,
): {
  keyTakeaways: string;
  agreementsDecisions: string;
  nextSteps: string;
} {
  return {
    keyTakeaways: t('structuredNote.sections.keyTakeaways'),
    agreementsDecisions: t('structuredNote.sections.agreementsDecisions'),
    nextSteps: t('structuredNote.sections.nextSteps'),
  };
}

/**
 * Initializes field state from a stored value.
 * Structured payloads open in display mode; plain text opens in compose-plain.
 */
export function createInitialFieldState(raw: string | null | undefined): StructuredNoteFieldState {
  const parsed = parseNoteValue(raw);

  if (parsed.mode === 'structured') {
    const structuredNote = normalizeSections(parsed.payload.structuredNote);
    return {
      viewMode: 'display',
      originalContent: parsed.payload.originalNote.content,
      structuredNote,
      lastAiSnapshot: createAiSnapshot(structuredNote),
    };
  }

  return {
    viewMode: 'compose-plain',
    originalContent: parsed.content,
    structuredNote: { ...EMPTY_STRUCTURED_NOTE },
    lastAiSnapshot: null,
  };
}
